/**
 * @typedef {import('../../shared/OfferForm/OfferFields').default} OfferFields
 * @typedef {import('../../shared/OfferService/AddsAgentOffer').default} AddsAgentOffer
 * @typedef {import('../../shared/OfferService/AddsOffer').default} AddsOffer
 * @typedef {import('../../shared/OfferService/AddsOfferImage').default} AddsOfferImage
 * @typedef {import('../../shared/OfferService/AddsGroupOffer').default} AddsGroupOffer
 * @typedef {import('../../shared/OfferService/EditsOffer').default} EditsOffer
 * @typedef {import('../../shared/OfferService/GetsAgentOffers').default} GetsAgentOffers
 * @typedef {import('../../shared/OfferService/GetsGroupOffers').default} GetsGroupOffers
 * @typedef {import('../../shared/OfferService/GetsSingleOffer').default} GetsSingleOffer
 * @typedef {import('../../shared/OfferService/Offer').default} OfferPayload
 * @typedef {import('../../shared/OfferService/OffersService').default} OffersService
 * @typedef {import('./ImageData')} ImageData
 * @typedef {{
 * 	errorMessage: string | null;
 * 	formComplete: boolean;
 * 	image: File | null;
 * 	isGettingData: boolean;
 * 	offerData: OfferFields;
 * }} EditOfferState
 * @typedef {{
 * 	location: {
 * 		state: {
 * 			offerId: string;
 * 			vendorMode?: 'agent' | 'group' | 'tenant' | 'local';
 * 		};
 * 	};
 * 	offerService: OffersService;
 * }} EditOfferProps
 */
import { file as validateFile } from 'shared/validators';
import { Redirect } from 'react-router-dom';
import { translate } from 'App/Internationalization';
import { withOfferService } from 'service-container';
import getErrorMessage from 'utils/getErrorMessage';
import mimeList from '@mooveguru/yhh-shared-config/files/allowed-mimes.json';
import Loading from 'shared/Loading';
import OfferForm from 'App/shared/OfferForm/OfferForm';
import paths from 'config/local/paths';
import React from 'react';
import { normalizePostalCodeList } from 'utils/postalCodes';

/** @extends {React.Component<EditOfferProps, EditOfferState>} */
export class EditOffer extends React.Component {
	/**
	 * @param {EditOfferProps} props
	 */
	constructor(props) {
		super(props);

		this.state = {
			errorMessage: null,
			formComplete: false,
			image: null,
			isGettingData: true,
			offerData: {
				category: '',
				description: '',
				endDate: '',
				image: null,
				isActive: '',
				postalCode: '',
				startDate: '',
				title: '',
				vendorEmail: '',
				vendorName: '',
				vendorPhone: '',
				website: '',
			},
		};

		this.handleEditOfferSubmit = this.handleEditOfferSubmit.bind(this);
		this.handleFileUploadChange = this.handleFileUploadChange.bind(this);
		this.submitOfferImage = this.submitOfferImage.bind(this);
		this.submit = this.submit.bind(this);
		this.toOfferPayload = this.toOfferPayload.bind(this);
		this.validateUploadedImage = this.validateUploadedImage.bind(this);
	}

	async componentDidMount() {
		await this.getAndSetInitialValues();
	}

	async getAndSetInitialValues() {
		try {
			const offer = await this.props.offerService.getSingleOffer(
				this.props.location.state.offerId
			);

			this.setState({
				isGettingData: false,
				offerData: {
					...offer,
					isActive: offer.isActive ? 'active' : 'disabled',
				},
			});
		} catch (error) {
			this.setState({
				errorMessage: getErrorMessage(error),
				isGettingData: false,
			});
		}
	}

	/**
	 * @param {OfferFields} values
	 * @returns {Promise<void>}
	 */
	async handleEditOfferSubmit(values) {
		try {
			this.validateUploadedImage();
		} catch (error) {
			this.setState({
				errorMessage:
					error instanceof Error
						? error.message
						: translate('admin.pages.offers.image_upload.error'),

				formComplete: false,
			});

			return;
		}

		try {
			await this.submit(values);
		} catch (error) {
			this.setState({ errorMessage: getErrorMessage(error) });
		}
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 */
	handleFileUploadChange(event) {
		if (!event.target.files?.length) {
			return;
		}
		this.setState({ image: event.target.files[0] });
	}

	/**
	 * @param {OfferFields} offer
	 * @returns {OfferPayload}
	 */
	toOfferPayload(offer) {
		return {
			category: offer.category,
			description: offer.description,
			endDate: offer.endDate,
			id: offer.id,
			image: offer.image,
			isActive: offer.isActive,
			offerImage: '',
			owner: offer.owner,
			postalCode:
				this.props.location?.state?.vendorMode === 'local'
					? normalizePostalCodeList(offer.postalCode)
					: offer.postalCode,
			startDate: offer.startDate,
			title: offer.title,
			vendorEmail: offer.vendorEmail,
			vendorName: offer.vendorName,
			vendorPhone: offer.vendorPhone,
			website: offer.website,
		};
	}

	/** @returns {Promise<void>} */
	async submitOfferImage() {
		if (this.state.image === null) {
			return;
		}

		try {
			await this.props.offerService.addOfferImage(
				this.state.image,
				this.props.location.state.offerId
			);
		} catch (error) {
			this.setState({ errorMessage: getErrorMessage(error) });
		}
	}

	/**
	 * @param {OfferFields} offer
	 * @returns {Promise<void>}
	 */
	async submit(offer) {
		const payload = this.toOfferPayload(offer);

		await this.props.offerService.editOffer(
			payload,
			this.props.location.state.offerId
		);

		if (this.state.image) {
			await this.submitOfferImage();
		}

		this.setState({
			errorMessage: null,
			formComplete: true,
		});
	}

	/**
	 * @returns {void}
	 * @throws {Error}
	 */
	validateUploadedImage() {
		if (!this.state.image) {
			return;
		}

		validateFile(this.state.image, mimeList.images, 'image');
	}

	/**
	 * @param {OfferFields['image']} image
	 * @returns {string | null}
	 */
	getPreview(image) {
		if (
			image !== null &&
			image !== undefined &&
			typeof image === 'object' &&
			'src' in image
		) {
			return image.src ?? null;
		}

		return null;
	}

	render() {
		const viewRoute = paths.app.admin.offers.view;
		const successMessage = translate('admin.pages.offers.edit_message');
		const vendorMode = this.props.location?.state?.vendorMode ?? 'agent';
		const preview = this.getPreview(this.state.offerData.image);

		if (this.state.isGettingData) {
			return <Loading />;
		}

		if (this.state.errorMessage !== null) {
			return <p>{this.state.errorMessage}</p>;
		}

		if (this.state.formComplete) {
			return (
				<Redirect
					to={{
						pathname: viewRoute,
						state: {
							message: successMessage,
							vendorMode,
						},
					}}
				/>
			);
		}

		return (
			<OfferForm
				allowPostalCodeList={
					this.props.location?.state?.vendorMode === 'local'
				}
				cancelPath={viewRoute}
				formSubmitText={translate('global.offers.update')}
				formTitle={translate('global.offers.edit_title')}
				hasGroupField={false}
				hasImageField={true}
				initialValues={this.state.offerData}
				onChange={this.handleFileUploadChange}
				onSubmit={this.handleEditOfferSubmit}
				preview={preview ?? undefined}
			/>
		);
	}
}

export default withOfferService(
	/** @type {React.ComponentType<{[key: string]: unknown, offerService: Partial<import('../../shared/OfferService/OffersService').default>}>} */ (
		/** @type {unknown} */ (EditOffer)
	)
);
